import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import analytics from '../../../modules/analytics';
import { selectors } from '../../../store/CurrentUser.model';
import withStore from '../../withStore';
import type { IRootDispatch, RootState } from '../../../store/models';

import { ClientNavBar } from './ClientNavBar';
import { useIsMobile, useLinkTo, useMount } from '../../../hooks';
import { useClientNavBarState, useShouldShowClientNavBar } from './hooks';
import { getSelectedItemFromCurrentRoute } from './getSelectedItemFromCurrentRoute';
import { CLIENT_APPTS_LIST } from '../../../route-names';

export const CLIENT_NAVBAR_CLICKED = 'client_navbar_item_clicked';

export const ClientNavBarContainer: React.FC = () => {
  const [selectedItem, setSelectedItem] = useState(null);
  const currentRouteName = useSelector<RootState, string>(({ route }) => route.name);
  const isLoggedIn: boolean = useSelector(selectors.isLoggedIn);
  const linkTo = useLinkTo();
  const isMobile = useIsMobile();
  const searchModel = useSelector<RootState, RootState['search']>(state => state.search);
  const showToolTips = useSelector<RootState, boolean>(state => state.clientNavBar.showTooltips);

  const {
    homeBubbleTooltipProps,
    handleCloseHomeBubbleTooltip,
    showHomeUnreadBadge,
    onClientNavBarRendered,
  } = useClientNavBarState();

  const shouldShow = useShouldShowClientNavBar();

  const dispatch = useDispatch<IRootDispatch>();

  useMount(async () => {
    await dispatch.clientNavBar.closeHomeBubbleTooltip();
    await dispatch.favorites.onClientNavBarRendered();
    onClientNavBarRendered();
  });

  useEffect(() => {
    const selectedItemFromRoute = getSelectedItemFromCurrentRoute(currentRouteName);
    // only overwrite selectedItem if not null
    if (selectedItemFromRoute) setSelectedItem(selectedItemFromRoute);
  }, [currentRouteName]);

  const handleTracking = (navbarItemName: string, extra?: { [key: string]: any }) => {
    analytics.track(CLIENT_NAVBAR_CLICKED, { navbar_item: navbarItemName, ...extra });
  };

  const handleHomePress = () => {
    handleTracking('home', { unread_badge: showHomeUnreadBadge });
    setSelectedItem('home');
    linkTo('home');
  };

  const handleSearchPress = () => {
    handleTracking('search');
    if (!currentRouteName.startsWith('search')) {
      setSelectedItem('search');
      linkTo('search', {
        source: 'client_navbar',
        q: searchModel.query,
      });
    }
  };

  const handleAppointmentsPress = () => {
    handleTracking('appts');
    setSelectedItem('appts');
    linkTo(CLIENT_APPTS_LIST);
  };

  const handleAccountPress = () => {
    handleTracking('account');
    setSelectedItem('account');
    linkTo('consumer.account');
  };

  const handleMorePress = () => {
    handleTracking('more');
    setSelectedItem('more');
    linkTo('consumer.more');
  };

  if (!isMobile || !isLoggedIn || shouldShow.loading) {
    return null;
  }

  return (
    <ClientNavBar
      selectedItem={selectedItem}
      onHomePress={handleHomePress}
      onSearchPress={handleSearchPress}
      onAppointmentsPress={handleAppointmentsPress}
      onAccountPress={handleAccountPress}
      onMorePress={handleMorePress}
      onCloseHomeBubbleTooltip={handleCloseHomeBubbleTooltip}
      homeBubbleTooltipProps={showToolTips ? homeBubbleTooltipProps : null}
      showHomeUnreadBadge={showToolTips && showHomeUnreadBadge}
    />
  );
};

export const ClientNavBarContainerConnected = withStore(ClientNavBarContainer);
